import type { ChipColor } from '@/types/chip';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, TablePagination, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Event } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';

interface ScheduleItem {
  id: string;
  status: string;
  agreement?: { type: string; id: string };
  tutor?: { type: string; id: string };
  tutorName?: string;
  clientName?: string;
  subject?: string;
}

const STATUSES = ['Draft', 'Proposed', 'Accepted', 'Rejected', 'Active', 'Completed', 'Cancelled'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Proposed: 'info',
  Accepted: 'success',
  Rejected: 'error',
  Active: 'success',
  Completed: 'secondary',
  Cancelled: 'warning',
};

export const TutoringScheduleList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const { data: allSchedules } = useGetList('tutoring-schedules', { pagination: { page: 1, perPage: 1000 }, filter: filters });

  const schedules = allSchedules?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allSchedules?.length ?? 0,
    active: allSchedules?.filter((s: ScheduleItem) => s.status === 'Active').length ?? 0,
    proposed: allSchedules?.filter((s: ScheduleItem) => s.status === 'Proposed').length ?? 0,
    completed: allSchedules?.filter((s: ScheduleItem) => s.status === 'Completed').length ?? 0,
    cancelled: allSchedules?.filter((s: ScheduleItem) => s.status === 'Cancelled').length ?? 0,
  };

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Tutoring Schedules"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Proposed', value: String(stats.proposed), color: 'info.main' },
          { label: 'Completed', value: String(stats.completed), color: 'secondary.main' },
          { label: 'Cancelled', value: String(stats.cancelled), color: 'warning.main' },
        ]}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Schedule</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tutor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules?.map((schedule: ScheduleItem) => (
              <TableRow
                key={schedule.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/tutoring-schedules/${schedule.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Event sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500}>{schedule.id?.substring(0, 12) || '—'}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{schedule.tutorName || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{schedule.subject || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{schedule.clientName || '—'}</Typography></TableCell>
                <TableCell><Chip label={schedule.status} size="small" color={STATUS_COLORS[schedule.status]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allSchedules?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!schedules || schedules.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Event sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No tutoring schedules found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
